/**
 * Rozpoznanie prevadzky (tenanta) z URL -- cisty modul bez Reactu.
 *
 * Styri rezimy:
 *   root      rozvozy.sk/               marketing + pravne stranky (basename "")
 *   platform  rozvozy.sk/sprava/...     platformova sprava       (basename "/sprava")
 *   path      rozvozy.sk/veteran/...    prevadzka podla slugu    (basename "/veteran")
 *   host      palermo.sk/...            prevadzka podla domeny   (basename "")
 *
 * basename ide do <BrowserRouter> (index.js). React Router ho potom prida ku
 * kazdemu <Link>, navigate() aj <Navigate>, takze obrazovky pisu cesty ako
 * doteraz ("/admin") a pri vlastnej domene je to identicky kod bez vetvenia.
 *
 * `loc` je parameter (nie window.location), aby sa to dalo testovat --
 * window.location sa v jsdom prepisat neda.
 */

// Hostitelia, na ktorych sa prevadzka urcuje CESTOU. Vsetko ostatne je
// vlastna domena prevadzky (host mode). Pri novom prostredi (napr. dalsi
// staging) ho treba doplnit sem aj do CORS_ORIGINS* v backend/.env.
export const SHARED_HOSTS = new Set([
  "rozvozy.sk",
  "www.rozvozy.sk",
  "test.rozvozy.sk",
  "localhost",
  "127.0.0.1",
]);

export const PLATFORM_SEGMENT = "sprava";

// Stranky na koreni zdielanej domeny, ktore nie su prevadzkou. Zhodne
// s RESERVED_SLUGS v backend/services/tenantRegistry.js.
export const ROOT_PAGES = new Set(["vop", "ochrana-osobnych-udajov", "pre-prevadzky", "static"]);

// Zhodne s CHECK companies_slug_format_chk (migracia 005)
const SLUG_RE = /^[a-z0-9]([a-z0-9-]{0,38}[a-z0-9])?$/;

// Stare subdomeny prevadzok (veteran.rozvozy.sk) -- do 301 presmerovania
// bezia v host mode; backend ich pozna cez company_domains.
const LEGACY_SUBDOMAIN_SUFFIX = ".rozvozy.sk";

function prvySegment(pathname) {
  const raw = String(pathname || "/").split("/").filter(Boolean)[0] || "";
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

/**
 * @param {{ hostname: string, pathname: string }} loc
 * @returns {{ mode: "root"|"platform"|"path"|"host", slug: string|null, basename: string, hostname: string }}
 */
export function resolveTenant(loc) {
  const hostname = String(loc?.hostname || "").toLowerCase().replace(/\.$/, "");

  if (!SHARED_HOSTS.has(hostname)) {
    const legacySlug = hostname.endsWith(LEGACY_SUBDOMAIN_SUFFIX)
      ? hostname.slice(0, -LEGACY_SUBDOMAIN_SUFFIX.length).split(".").pop() || null
      : null;
    return { mode: "host", slug: legacySlug, basename: "", hostname };
  }

  const segment = prvySegment(loc?.pathname);
  const slug = segment.toLowerCase();

  if (!segment || ROOT_PAGES.has(slug)) {
    return { mode: "root", slug: null, basename: "", hostname };
  }
  if (slug === PLATFORM_SEGMENT) {
    return { mode: "platform", slug: null, basename: `/${segment}`, hostname };
  }
  if (SLUG_RE.test(slug)) {
    // basename s povodnym zapisom segmentu -- React Router ho porovnava bez
    // ohladu na velkost pismen, ale odrezava presne tolko znakov
    return { mode: "path", slug, basename: `/${segment}`, hostname };
  }
  // Nieco, co ako slug nevyzera (rozvozy.sk/%%%) -- koren s vlastnou 404
  return { mode: "root", slug: null, basename: "", hostname };
}

let aktualny = null;

/** Tenant aktualnej stranky. Pocas zivota stranky sa nemeni -- prechod na inu
 *  prevadzku je vzdy plne nacitanie (iny basename). */
export function getTenant() {
  if (!aktualny) aktualny = resolveTenant(window.location);
  return aktualny;
}

export const getTenantBasename = () => getTenant().basename;

/** Len pre testy. */
export function __setTenantForTests(tenant) {
  aktualny = tenant;
}
